import {
  BarChart3,
  BadgeDollarSign,
  Banknote,
  Calendar,
  Clock3,
  ChevronDown,
  Gift,
  LogOut,
  Package,
  Scissors,
  Settings,
  ShoppingBag,
  UserCog,
  Users,
  Menu,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { StaffAvatar } from '@/components/staff/StaffAvatar';
import { TerminalConnectionControl } from '@/components/terminal/TerminalConnectionControl';
import { useAuth } from '@/context/useAuth';
import { StripeTerminalProvider } from '@/context/StripeTerminalContext';
import { useCurrentStaff } from '@/hooks/useCurrentStaff';
import { cn } from '@/lib/utils';

const navigation = [
  { name: 'Calendar', href: '/', icon: Calendar },
  { name: 'Clients', href: '/clients', icon: Users },
  { name: 'Staff', href: '/staff', icon: UserCog },
  { name: 'Services', href: '/services', icon: Scissors },
  { name: 'Products', href: '/products', icon: Package },
  { name: 'Sales', href: '/sales', icon: ShoppingBag },
  { name: 'Cash Drawer', href: '/cash-drawer', icon: Banknote },
  { name: 'Gift Cards', href: '/gift-cards', icon: Gift },
  { name: 'Payroll', href: '/payroll', icon: BadgeDollarSign },
  { name: 'Time Clock', href: '/clock', icon: Clock3 },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
];

export function AppShell() {
  const { logout, user } = useAuth();
  const currentStaffQuery = useCurrentStaff();
  const navigate = useNavigate();
  const location = useLocation();
  const isKioskLocked = location.pathname === '/clock';
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (isKioskLocked && location.pathname !== '/clock') {
      navigate('/clock', { replace: true });
    }
  }, [isKioskLocked, location.pathname, navigate]);

  useEffect(() => {
    if (isKioskLocked) {
      setUserMenuOpen(false);
      setMobileNavOpen(false);
    }
  }, [isKioskLocked]);

  const currentStaff = currentStaffQuery.data;
  const displayName =
    currentStaff?.displayName ?? user?.displayName ?? user?.email?.split('@')[0] ?? 'Staff';
  const staffAvatarProps = {
    displayName,
    firstName: currentStaff?.firstName,
    lastName: currentStaff?.lastName,
    photoUrl: currentStaff?.photoUrl,
    photoStoragePath: currentStaff?.photoStoragePath,
    staffId: currentStaff?.id ?? user?.uid,
  };

  return (
    <StripeTerminalProvider>
      <div className="app-shell flex h-dvh flex-col overflow-hidden">
        {!isKioskLocked ? (
        <header className="top-nav">
          <div className="top-nav-inner">
            <div className="top-nav-left">
              <div className="top-nav-brand">
                <span className="top-nav-brand-mark">RN</span>
                <span className="top-nav-brand-name">Rodeo Nails</span>
              </div>

              <nav className="top-nav-links">
                {navigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    end={item.href === '/'}
                    className={({ isActive }) =>
                      cn('top-nav-link', isActive && 'top-nav-link-active')
                    }
                  >
                    <item.icon className="top-nav-link-icon" aria-hidden="true" />
                    <span>{item.name}</span>
                  </NavLink>
                ))}
              </nav>
            </div>

            <div className="top-nav-right">
              <TerminalConnectionControl className="top-nav-terminal-control" />

              <div className="top-nav-user">
                <button
                  className="top-nav-user-trigger"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  type="button"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <StaffAvatar
                    className="top-nav-avatar"
                    size="xs"
                    {...staffAvatarProps}
                  />
                  <div className="top-nav-user-info">
                    <span className="top-nav-user-name">{displayName}</span>
                    <span className="top-nav-user-org">Rodeo Nails</span>
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform',
                      userMenuOpen && 'rotate-180',
                    )}
                  />
                </button>

                {userMenuOpen && (
                  <>
                    <div
                      className="top-nav-user-backdrop"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="top-nav-user-menu">
                      <div className="top-nav-user-menu-header">
                        <StaffAvatar
                          className="top-nav-avatar top-nav-avatar-lg"
                          size="md"
                          {...staffAvatarProps}
                        />
                        <div>
                          <p className="font-medium text-foreground">{displayName}</p>
                          <p className="text-sm text-muted-foreground">
                            {user?.email ?? 'staff@rodeonails.com'}
                          </p>
                        </div>
                      </div>
                      <div className="top-nav-user-menu-divider" />
                      <button
                        className="top-nav-user-menu-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate('/settings');
                        }}
                        type="button"
                      >
                        <Settings className="h-4 w-4" />
                        Settings
                      </button>
                      <button
                        className="top-nav-user-menu-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          void logout();
                        }}
                        type="button"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>

              <button
                className="top-nav-mobile-toggle"
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                type="button"
                aria-label="Toggle navigation"
              >
                {mobileNavOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {mobileNavOpen && (
            <nav className="top-nav-mobile">
              <div className="top-nav-mobile-terminal">
                <TerminalConnectionControl compact />
              </div>
              {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/'}
                onClick={() => setMobileNavOpen(false)}
                className={({ isActive }) =>
                  cn('top-nav-mobile-link', isActive && 'top-nav-mobile-link-active')
                }
              >
                <item.icon className="h-5 w-5" aria-hidden="true" />
                <span>{item.name}</span>
              </NavLink>
            ))}
            <div className="top-nav-mobile-divider" />
            <button
              className="top-nav-mobile-link"
              onClick={() => {
                setMobileNavOpen(false);
                void logout();
              }}
              type="button"
            >
              <LogOut className="h-5 w-5" />
              <span>Sign out</span>
            </button>
          </nav>
        )}
      </header>
        ) : null}

        <main className={cn('top-nav-content', isKioskLocked && 'top-nav-content-kiosk')}>
          <Outlet />
        </main>
      </div>
    </StripeTerminalProvider>
  );
}
